import { ErrorHandler, Injectable, Injector, isDevMode } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

/**
 * Global Error Handler Service
 * Captures all unhandled errors in the application (non-HTTP)
 *
 * Features:
 * - Catches errors from try-catch blocks in services
 * - Logs errors to console with categorization
 * - Distinguishes between client errors and HTTP errors
 * - Does not interfere with HttpErrorHandlerService flow
 *
 * Note: HTTP errors are handled by HttpErrorHandlerService via catchError operator
 * This handler captures:
 * - localStorage/sessionStorage errors
 * - JSON parsing errors
 * - Null reference errors
 * - Type errors
 * - Any other runtime errors not caught by HTTP error handling
 */
@Injectable()
export class GlobalErrorHandlerService implements ErrorHandler {
  constructor(private readonly injector: Injector) {}

  /**
   * Handle all uncaught errors
   * @param error - The error object
   */
  handleError(error: Error | HttpErrorResponse): void {
    // HTTP errors are already handled by HttpErrorHandlerService
    // Only log them here for global tracking
    if (this.isHttpError(error)) {
      if (isDevMode()) {
        console.error('[Global Error Handler] HTTP Error detected:', {
          status: error.status,
          message: error.message,
          url: error.url
        });
      }
      return;
    }

    // Handle client-side errors
    this.handleClientError(error);
  }

  /**
   * Check if error is an HTTP error
   */
  private isHttpError(error: unknown): error is HttpErrorResponse {
    return error instanceof HttpErrorResponse;
  }

  /**
   * Handle client-side runtime errors
   */
  private handleClientError(error: Error): void {
    const errorDetails = {
      message: error.message,
      name: error.name,
      stack: error.stack,
      timestamp: new Date().toISOString()
    };

    // Categorize error type for better debugging
    const errorCategory = this.categorizeError(error);

    if (isDevMode()) {
      console.error(
        `[Global Error Handler] ${errorCategory} Error:`,
        errorDetails
      );
    }
  }

  /**
   * Categorize error for better debugging
   */
  private categorizeError(error: Error): string {
    const message = error.message.toLowerCase();

    if (
      message.includes('localstorage') ||
      message.includes('sessionstorage')
    ) {
      return 'Storage';
    }

    if (message.includes('json') || message.includes('parse')) {
      return 'JSON Parsing';
    }

    if (message.includes('null') || message.includes('undefined')) {
      return 'Null Reference';
    }

    if (error.name === 'TypeError') {
      return 'Type';
    }

    if (error.name === 'ReferenceError') {
      return 'Reference';
    }

    return 'Runtime';
  }
}
